import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";

import PageHeader from "components/Common/PageHeader";
import TableFilterDynamique from "components/Common/TableFilter/TableFilterDynamique";
import CustomButton from "components/Common/ui/CustomButton";

import AddEditTeacherJournalModal from "../modals/AddEditTeacherJournalModal";
import TeacherJournalTable from "../table/TeacherJournalTable";

import useTeacherJournal from "../hooks/useTeacherJournal";
import { FilterField } from "components/Common/TableFilter/TableFilter";
import { all_routes } from "router/all_routes";
import { useAbility } from "config/abilityContext";
import useClassManagement from "feature-module/academic/classes/hooks/useClassManagement";

const TeacherJournalsPage = () => {
    const { t } = useTranslation();
    const ability = useAbility();

    const {
        journals,
        infoPagination,
        loading,
        selectedJournal,
        setSelectedJournal,
        fetchTeacherJournals,
        handleCreate,
        handleUpdate,
        handleDelete,
    } = useTeacherJournal();

    const [isModalOpen, setIsModalOpen] = useState(false);

    const { fetchClasses, classes } = useClassManagement();

    const breadcrumbItems = [
        { label: "dashboard", path: all_routes.dashboard },
        { label: t("teacherJournal.title"), active: true },
    ];

    const [filters, setFilters] = useState<{ [key: string]: any }>({});

    useEffect(() => {
        fetchTeacherJournals({ page: 1, limit: 10 });
        fetchClasses();
    }, [fetchTeacherJournals]);

    /**
     * 🔹 Options Classe
     */
    const classOptions = useMemo(
        () =>
            (classes || []).map((c: any) => ({
                label: c.name,
                value: c.id,
            })),
        [classes]
    );


    const subjectOptions = useMemo(() => {
        if (!classes || classes.length === 0) return [];

        if (filters.classId) {
            const cls = classes.find((c: any) => c.id === filters.classId);
            if (!cls || !cls.classSubject) return [];
            return cls.classSubject
                .filter((cs: any) => cs.subject)
                .map((cs: any) => ({
                    label: cs.subject.name,
                    value: cs.subject.id,
                }));
        }

        const map = new Map<string, { label: string; value: string }>();
        classes.forEach((c: any) => {
            (c.classSubject || []).forEach((cs: any) => {
                if (cs.subject && !map.has(cs.subject.id)) {
                    map.set(cs.subject.id, {
                        label: cs.subject.name,
                        value: cs.subject.id,
                    });
                }
            });
        });

        return Array.from(map.values());
    }, [classes, filters.classId]);


    const teacherOptions = useMemo(() => {
        if (!classes || classes.length === 0) return [];

        const addTeacherToMap = (
            map: Map<string, { label: string; value: string }>,
            cs: any
        ) => {
            if (!cs.teacher || !cs.teacher.id) return;
            const teacherId = cs.teacher.id;
            if (map.has(teacherId)) return;

            const user = cs.teacher.user;
            const label = user
                ? `${user.firstName} ${user.lastName}`
                : teacherId;

            map.set(teacherId, { label, value: teacherId });
        };

        const map = new Map<string, { label: string; value: string }>();

        if (filters.classId) {
            const cls = classes.find((c: any) => c.id === filters.classId);
            if (!cls || !cls.classSubject) return [];
            cls.classSubject.forEach((cs: any) => addTeacherToMap(map, cs));
            return Array.from(map.values());
        }

        classes.forEach((c: any) => {
            (c.classSubject || []).forEach((cs: any) => addTeacherToMap(map, cs));
        });

        return Array.from(map.values());
    }, [classes, filters.classId]);


    const filterFields: FilterField[] = [
        {
            key: "classId",
            label: t("class"),
            type: "select",
            options: classOptions,
        },
        {
            key: "subjectId",
            label: t("subject"),
            type: "select",
            options: subjectOptions,
        },
        {
            key: "teacherId",
            label: t("teacher"),
            type: "select",
            options: teacherOptions,
        },
        {
            key: "date",
            label: t("date"),
            type: "range",
            rangeKeys: { start: "dateStart", end: "dateEnd" },
        },
    ];

    const handleSaveJournal = async (journalData: any) => {
        let res = false;

        if (selectedJournal) {
            res = await handleUpdate(selectedJournal.id, journalData);
        } else {
            res = await handleCreate(journalData);
        }

        if (res) {
            setIsModalOpen(false);
            setSelectedJournal(null);
            fetchTeacherJournals({ page: 1, limit: 10, ...filters });
        }
    };

    const handleEdit = (record: any) => {
        setSelectedJournal(record);
        setIsModalOpen(true);
    };

    const handleDeleteTeacherJournal = async (id: string) => {
        const res = await handleDelete(id);
        if (res) {
            fetchTeacherJournals({ page: 1, limit: 10, ...filters });
        }
    };

    const handleFilter = (newFilters: any) => {
        const params = {
            ...newFilters,
            page: 1,
            limit: 10,
        };

        setFilters(newFilters);
        fetchTeacherJournals(params);
    };

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <PageHeader
                    title={t("teacherJournal.title")}
                    breadcrumbItems={breadcrumbItems}
                />

                <div className="d-flex mb-3 justify-content-end gap-2">
                    {ability.can("create", "TeacherJournal") && (
                        <CustomButton
                            onClick={() => {
                                setSelectedJournal(null);
                                setIsModalOpen(true);
                            }}
                        >
                            <i className="ti ti-plus me-2" />
                            {t("teacherJournal.add_title")}
                        </CustomButton>
                    )}

                    <TableFilterDynamique
                        fields={filterFields}
                        filters={filters}
                        onApply={handleFilter}
                        onReset={() => {
                            setFilters({});
                            fetchTeacherJournals({ page: 1, limit: 10 });
                        }}
                    />
                </div>

                <TeacherJournalTable
                    dataSource={journals ?? []}
                    loading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDeleteTeacherJournal}
                    dataPaginate={{
                        current: infoPagination?.page || 1,
                        pageSize: infoPagination?.limit || 10,
                        total: infoPagination?.total,
                    }}
                    onChange={(pg) => {
                        const params = {
                            ...filters,
                            page: pg.current || 1,
                            limit: pg.pageSize || 10,
                        };
                        fetchTeacherJournals(params);
                    }}
                />

                <AddEditTeacherJournalModal
                forTeacher={false}
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    journalData={selectedJournal}
                    onSave={handleSaveJournal}
                    classes={classes}
                />
            </div>
        </div>
    );
};

export default TeacherJournalsPage;
